import React, { useState, useEffect, useMemo } from 'react';
import { Button, Badge } from "../ui";
import { MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter, MDBInput, MDBSpinner, MDBTable, MDBTableHead, MDBTableBody } from "mdb-react-ui-kit";

import { api } from '../../services';
import { getImageUrl } from '../../utils/utils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';

const DEFAULT_STOCK_VALUE = 100;
const DEFAULT_ORDER_COUNT = 100;

const AddInventoryModal = ({ show, setShow, onClose }) => {
    const {t, i18n} = useTranslation("translation");
  const isArabic = i18n.language === "ar";
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [stockValues, setStockValues] = useState({});
    const [orderCountValues, setOrderCountValues] = useState({});
    const [unlimitedOrderValues, setUnlimitedOrderValues] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [inventoryDate, setInventoryDate] = useState(new Date());
    const [existingInventory, setExistingInventory] = useState({});
    const [store, setStore] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState('');

    const filteredProducts = useMemo(() =>
        products.filter(product =>
            product.name?.en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.name?.ar?.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [products, searchTerm]
    );

    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');

                // Fetch store
                const storeRes = await api.get('/stores/my', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const storeData = storeRes.data?.stores?.[0];
                if (!storeData) throw new Error('No store found');
                setStore(storeData);

                // Fetch branches
                const branchesRes = await api.get(`/stores/${storeData._id}/branches`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const branchList = branchesRes.data?.branches || [];
                setBranches(branchList);
                if (!selectedBranchId && branchList.length > 0) {
                    setSelectedBranchId(branchList[0]._id); // ✅ This will trigger the second useEffect
                }

                // Fetch products
                const productsRes = await api.get('/products-dropdown', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setProducts(productsRes.data.items);

            } catch (err) {
                console.error('Failed to fetch data:', err.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (show) fetchInitialData();
    }, [show]);
    
    useEffect(() => {
        const fetchInventory = async () => {
            if (!selectedBranchId) return;

            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const dateStr = inventoryDate.toISOString().split('T')[0];

                const inventoryRes = await api.get(`/inventory?page=all&date=${dateStr}&branchId=${selectedBranchId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const inventoryMap = {};
                inventoryRes.data.inventory.forEach(item => {
                    inventoryMap[item.product._id] = item;
                });
                setExistingInventory(inventoryMap);
            } catch (err) {
                console.error('Failed to fetch inventory:', err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInventory();
    }, [inventoryDate, selectedBranchId]);

    useEffect(() => {
        if (selectAll) {
            const allProductIds = filteredProducts.map(p => p._id);
            setSelectedProducts(allProductIds);

            const defaultValues = allProductIds.reduce((acc, id) => {
                const existingItem = existingInventory[id];
                acc.stocks[id] = existingItem?.stock || DEFAULT_STOCK_VALUE;
                acc.counts[id] = existingItem?.order_count || DEFAULT_ORDER_COUNT;
                acc.unlimited[id] = existingItem?.unlimited_orders || false;
                return acc;
            }, { stocks: {}, counts: {}, unlimited: {} });

            setStockValues(defaultValues.stocks);
            setOrderCountValues(defaultValues.counts);
            setUnlimitedOrderValues(defaultValues.unlimited);
        } else {
            setSelectedProducts([]);
            setStockValues({});
            setOrderCountValues({});
            setUnlimitedOrderValues({});
        }
    }, [selectAll, filteredProducts, existingInventory]);

    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => {
            const newSelected = prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId];

            setSelectAll(newSelected.length === filteredProducts.length);
            return newSelected;
        });

        if (!selectedProducts.includes(productId)) {
            const existingItem = existingInventory[productId];
            setStockValues(prev => ({
                ...prev,
                [productId]: existingItem?.stock || DEFAULT_STOCK_VALUE
            }));
            setOrderCountValues(prev => ({
                ...prev,
                [productId]: existingItem?.order_count || DEFAULT_ORDER_COUNT
            }));
            setUnlimitedOrderValues(prev => ({
                ...prev,
                [productId]: existingItem?.unlimited_orders || false
            }));
        }
    };

    const handleStockChange = (productId, value) => {
        setStockValues(prev => ({
            ...prev,
            [productId]: parseInt(value, 10) || 0
        }));
    };

    const handleOrderCountChange = (productId, value) => {
        setOrderCountValues(prev => ({
            ...prev,
            [productId]: parseInt(value, 10) || 0
        }));
    };

    const handleUnlimitedToggle = (productId) => {
        setUnlimitedOrderValues(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const handleSubmit = async () => {
        if (!selectedBranchId) {
            alert(`${t(`${t("Please select a branch.")}`)}`);
            return;
        }

        if (selectedProducts.length === 0) {
            alert(`${t("Please select at least one product.")}`);
            return;
        }

        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const dateStr = inventoryDate.toISOString().split('T')[0];

            const payloads = selectedProducts.map(productId => ({
                date: dateStr,
                product: productId,
                branch: selectedBranchId,
                stock: stockValues[productId] || DEFAULT_STOCK_VALUE,
                order_count: unlimitedOrderValues[productId] ? null : (orderCountValues[productId] || DEFAULT_ORDER_COUNT),
                unlimited_orders: unlimitedOrderValues[productId] || false
            }));
            const finalPayload = {
                inventory: payloads
            };

            await api.post('inventory/bulk', finalPayload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            alert(`${selectedProducts.length} ${t("item(s) added to inventory!")}`);
            setShow(false);
            onClose();
        } catch (err) {
            console.error(err);
            alert(`${t("Failed to add inventory items")}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <MDBModal open={show} setShow={setShow} tabIndex='-1' size='xl' fullscreen="lg-down">
            <MDBModalDialog size="xl" scrollable>
                <MDBModalContent>
                    <MDBModalHeader className="align-items-center">
                        <MDBModalTitle className="d-flex align-items-center">
                            {t("addinventoryitem")}
                            {selectedProducts.length > 0 && (
                                <Badge color='primary' className='ms-2'>
                                    {selectedProducts.length} selected
                                </Badge>
                            )}
                        </MDBModalTitle>
                        <Button className='btn-close' color='none' onClick={() => setShow(false)} />
                    </MDBModalHeader>

                    <MDBModalBody className="p-4">
                        <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-4">
                            <div className="flex-grow-1">
                                <label className="form-label d-block"> {t("inventorydate")}</label>
                                <DatePicker
                                    selected={inventoryDate}
                                    onChange={setInventoryDate}
                                    className="form-control w-100"
                                    dateFormat="yyyy-MM-dd"
                                    minDate={new Date()}
                                />
                            </div>
                            <div className="flex-grow-1">
                                <label> {t("searchproduct")}</label>
                                <MDBInput
                                    label= {t("searchproduct")}
                                    type='text'
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-100"
                                />
                            </div>
                            <div className="flex-grow-1">
                                <label className="form-label d-block"> {t("selectbranch")}</label>
                                <select
                                    className="form-select"
                                    value={selectedBranchId}
                                    onChange={(e) => setSelectedBranchId(e.target.value)}
                                >
                                    <option value="" disabled>Select a branch</option>
                                    {branches.map(branch => (
                                        <option key={branch._id} value={branch._id}>
                                            {branch.name?.en || branch.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="d-flex align-items-end">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={(e) => setSelectAll(e.target.checked)}
                                        id="selectAllCheckbox"
                                    />
                                    <label className="form-check-label" htmlFor="selectAllCheckbox">
                                        {t("selectall")}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Product Table */}
                        {isLoading ? (
                            <div className="text-center py-5">
                                <MDBSpinner grow size='lg' className='mx-2' color='primary' />
                                <p className="mt-3">Loading products...</p>
                            </div>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                  <MDBTable hover align="middle">
  <MDBTableHead className="bg-primary text-white sticky-top">
    <tr>
      <th  className="text-nowrap custom-th"></th>
      <th className="text-nowrap custom-th"> {t("product")}</th>
      <th className="text-nowrap custom-th"> {t("initialstock")}</th>
      <th className="text-nowrap custom-th"> {t("unlimitedorders")}</th>
      <th className="text-nowrap custom-th"> {t("ordercount")}</th>
      <th className="text-nowrap custom-th"> {t("status")}</th>
    </tr>
  </MDBTableHead>
  <MDBTableBody>
    {filteredProducts.map((product) => {
      const isSelected = selectedProducts.includes(product._id);
      const existingItem = existingInventory[product._id];

      return (
        <tr key={product._id}>
          <td>
            <input
              type="checkbox"
              className="form-check-input"
              checked={isSelected}
              onChange={() => handleProductSelect(product._id)}
            />
          </td>
          <td>
            <div className="d-flex align-items-center">
              <img
                src={getImageUrl(product.image_path)}
                alt={product.name?.en}
                className="rounded me-3"
                style={{
                  width: "50px",
                  height: "50px",
                  objectFit: "cover"
                }}
              />
              <div>
                <div className="fw-bold">
                  {product.name?.en || product.name}
                </div>
                <small className="text-muted">
                  SKU: {product._id || 'N/A'}
                </small>
              </div>
            </div>
          </td>
          <td>
            <MDBInput
              type='number'
              min='0'
              value={isSelected ? (stockValues[product._id] || DEFAULT_STOCK_VALUE) : ''}
              onChange={(e) => handleStockChange(product._id, e.target.value)}
              disabled={!isSelected}
              className="m-0"
            />
          </td>
          <td>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={isSelected ? (unlimitedOrderValues[product._id] || false) : false}
                onChange={() => handleUnlimitedToggle(product._id)}
                disabled={!isSelected}
              />
            </div>
          </td>
          <td>
            <MDBInput
              type='number'
              min='0'
              value={isSelected ? (
                unlimitedOrderValues[product._id]
                  ? ''
                  : (orderCountValues[product._id] || DEFAULT_ORDER_COUNT)
              ) : ''}
              onChange={(e) => handleOrderCountChange(product._id, e.target.value)}
              disabled={!isSelected || unlimitedOrderValues[product._id]}
              placeholder={unlimitedOrderValues[product._id] ? "Unlimited" : ""}
              className="m-0"
            />
          </td>
          <td>
            {existingItem && (
              <Badge color="warning">{t("existing")}</Badge>
            )}
          </td>
        </tr>
      );
    })}
  </MDBTableBody>
</MDBTable>

                            </div>
                        )}
                    </MDBModalBody>

                    <MDBModalFooter className="d-flex justify-content-between">
                        <div>
                            {selectedProducts.length > 0 && (
                                <span className="text-muted">
                                    {selectedProducts.length} product(s) selected
                                </span>
                            )}
                        </div>
                        {isArabic ? <div>
                            <Button
                                color='primary'
                                onClick={handleSubmit}
                                disabled={selectedProducts.length === 0 || isLoading || !selectedBranchId}
                            >
                                {isLoading ? (
                                    <>
                                        <MDBSpinner size='sm' className='me-2' />
                                        Saving...
                                    </>
                                ) : (
                                    `${t("save")}`
                                )}
                            </Button>
                            <Button
                                color='secondary'
                                onClick={() => setShow(false)}
                                className="me-2"
                                disabled={isLoading}
                            >
                                 {t("cancel")}
                            </Button>
                        </div> : <div>
                            <Button
                                color='secondary'
                                onClick={() => setShow(false)}
                                className="me-2"
                                disabled={isLoading}
                            >
                                 {t("cancel")}
                            </Button>
                            <Button
                                color='primary'
                                onClick={handleSubmit}
                                disabled={selectedProducts.length === 0 || isLoading || !selectedBranchId}
                            >
                                {isLoading ? (
                                    <>
                                        <MDBSpinner size='sm' className='me-2' />
                                        Saving...
                                    </>
                                ) : (
                                    `${t("save")}`
                                )}
                            </Button>
                        </div>}
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
};

export default AddInventoryModal;
